/**
 * IncidentDetailPanel Component - Phase 13
 * 
 * Detail view for a single incident with actions.
 */

import { useState } from 'react';
import {
  Clock,
  User,
  MapPin,
  Monitor,
  AlertCircle,
  CheckCircle,
  XCircle,
  MessageSquare,
  ExternalLink,
  Copy,
  Tag,
  FileText,
  RefreshCw,
  AlertTriangle,
  Flag,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { getDomainDisplayLabel, getSourceTypeDisplayLabel } from '@/lib/offlineAudit';
import { copyText } from '@/lib/copyText';

// Severity badge config
const SEVERITY_CONFIG = {
  critical: { color: 'bg-red-500', textColor: 'text-white', icon: AlertTriangle },
  error: { color: 'bg-orange-500', textColor: 'text-white', icon: XCircle },
  warning: { color: 'bg-yellow-500', textColor: 'text-black', icon: AlertCircle },
  info: { color: 'bg-blue-500', textColor: 'text-white', icon: CheckCircle }
};

// Status badge config
const STATUS_CONFIG = {
  observed: { color: 'bg-gray-100 text-gray-700', label: 'Observed' },
  queued: { color: 'bg-blue-100 text-blue-700', label: 'Queued' },
  retrying: { color: 'bg-blue-100 text-blue-700', label: 'Retrying' },
  resolved: { color: 'bg-green-100 text-green-700', label: 'Resolved' },
  failed: { color: 'bg-red-100 text-red-700', label: 'Failed' },
  conflict: { color: 'bg-purple-100 text-purple-700', label: 'Conflict' },
  manual_review: { color: 'bg-amber-100 text-amber-700', label: 'Manual Review' },
  dismissed: { color: 'bg-gray-100 text-gray-700', label: 'Dismissed' }
};

// Format timestamp
const formatTimestamp = (ts) => {
  if (!ts) return 'N/A';
  return new Date(ts).toLocaleString();
};

// Format time ago
const formatTimeAgo = (ts) => {
  if (!ts) return 'Unknown';
  const mins = Math.floor((Date.now() - ts) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

/**
 * Detail Row Component
 */
function DetailRow({ label, value, icon: Icon, copyable, mono }) {
  if (!value && value !== 0) return null;
  
  const handleCopy = () => {
    copyText(String(value), 'Copied to clipboard');
  };
  
  return (
    <div className="flex items-start gap-3 py-2">
      {Icon && <Icon className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />}
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className={cn(
          "text-sm truncate",
          mono && "font-mono text-xs"
        )}>
          {value}
        </p>
      </div>
      {copyable && (
        <Button variant="ghost" size="sm" onClick={handleCopy} className="h-6 w-6 p-0">
          <Copy className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
}

/**
 * IncidentDetailPanel Component
 */
export function IncidentDetailPanel({
  incident,
  open,
  onClose,
  onAddNote,
  onMarkReviewed,
  onMarkNeedsFollowup,
  onMarkEscalated,
  onDismiss,
  currentUser
}) {
  const [noteText, setNoteText] = useState('');
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  if (!incident) return null;

  const severityConfig = SEVERITY_CONFIG[incident.severity] || SEVERITY_CONFIG.info;
  const statusConfig = STATUS_CONFIG[incident.status] || STATUS_CONFIG.observed;
  const SeverityIcon = severityConfig.icon;

  // Handle actions
  const handleAction = async (action, handler, ...args) => {
    setActionLoading(action);
    try {
      const result = await handler(...args);
      if (result?.success) {
        toast.success(`Incident ${action}`);
        if (action === 'note added') {
          setNoteText('');
          setShowNoteInput(false);
        }
      } else {
        toast.error(result?.error || 'Action failed');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddNote = () => {
    if (!noteText.trim()) return;
    handleAction('note added', onAddNote, incident.incidentId, noteText, currentUser);
  };

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent className="w-full sm:max-w-lg" data-testid="incident-detail-panel">
        <SheetHeader className="pb-4">
          <div className="flex items-start gap-3">
            <div className={cn(
              "p-2 rounded-lg flex-shrink-0",
              severityConfig.color
            )}>
              <SeverityIcon className={cn("h-5 w-5", severityConfig.textColor)} />
            </div>
            <div className="flex-1 min-w-0">
              <SheetTitle className="text-lg leading-tight">
                {incident.title}
              </SheetTitle>
              <SheetDescription className="mt-1">
                {formatTimeAgo(incident.occurredAt)}
              </SheetDescription>
            </div>
          </div>
          
          <div className="flex items-center gap-2 mt-3">
            <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
            <Badge variant="outline">{getDomainDisplayLabel(incident.domain)}</Badge>
            <Badge variant="outline">{getSourceTypeDisplayLabel(incident.sourceType)}</Badge>
          </div>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-280px)]">
          <div className="space-y-6 pr-4">
            {/* Message */}
            {incident.message && (
              <div>
                <Label className="text-xs text-muted-foreground">Message</Label>
                <p className="mt-1 text-sm bg-muted p-3 rounded-lg">
                  {incident.message}
                </p>
              </div>
            )}

            {/* Timestamps */}
            <div>
              <Label className="text-xs text-muted-foreground mb-2 block">Timestamps</Label>
              <div className="space-y-1">
                <DetailRow icon={Clock} label="Occurred At" value={formatTimestamp(incident.occurredAt)} />
                <DetailRow icon={Clock} label="Created At" value={formatTimestamp(incident.createdAt)} />
                {incident.resolvedAt && (
                  <DetailRow icon={CheckCircle} label="Resolved At" value={formatTimestamp(incident.resolvedAt)} />
                )}
              </div>
            </div>

            <Separator />

            {/* Actor */}
            {incident.actor?.userId && (
              <div>
                <Label className="text-xs text-muted-foreground mb-2 block">Actor</Label>
                <DetailRow icon={User} label="User ID" value={incident.actor.userId} copyable mono />
                {incident.actor.name && (
                  <DetailRow icon={User} label="Name" value={incident.actor.name} />
                )}
                {incident.actor.role && (
                  <DetailRow label="Role" value={incident.actor.role} />
                )}
              </div>
            )}

            {/* Location / Terminal */}
            {(incident.locationId || incident.terminalId) && (
              <div>
                <Label className="text-xs text-muted-foreground mb-2 block">Location</Label>
                {incident.locationId && (
                  <DetailRow icon={MapPin} label="Location ID" value={incident.locationId} copyable mono />
                )}
                {incident.terminalId && (
                  <DetailRow icon={Monitor} label="Terminal ID" value={incident.terminalId} copyable mono />
                )}
                {incident.deviceId && (
                  <DetailRow label="Device ID" value={incident.deviceId} copyable mono />
                )}
              </div>
            )}

            <Separator />

            {/* Related Entities */}
            {(incident.queueItemId || incident.localOrderId || incident.serverOrderId || incident.paymentIntentId) && (
              <div>
                <Label className="text-xs text-muted-foreground mb-2 block">Related Entities</Label>
                <DetailRow icon={FileText} label="Queue Item ID" value={incident.queueItemId} copyable mono />
                {incident.localOrderId && (
                  <DetailRow label="Local Order ID" value={incident.localOrderId} copyable mono />
                )}
                {incident.serverOrderId && (
                  <DetailRow label="Server Order ID" value={incident.serverOrderId} copyable mono />
                )}
                {incident.paymentIntentId && (
                  <DetailRow label="Payment Intent ID" value={incident.paymentIntentId} copyable mono />
                )}
                {incident.relatedEntityType && (
                  <DetailRow label="Entity Type" value={incident.relatedEntityType} />
                )}
                {incident.relatedEntityId && (
                  <DetailRow label="Entity ID" value={incident.relatedEntityId} copyable mono />
                )}
              </div>
            )}

            {/* Tags */}
            {incident.tags && incident.tags.length > 0 && (
              <div>
                <Label className="text-xs text-muted-foreground mb-2 block">Tags</Label>
                <div className="flex flex-wrap gap-1">
                  {incident.tags.map((tag, idx) => (
                    <Badge key={idx} variant="outline" className="text-xs">
                      <Tag className="h-3 w-3 mr-1" />
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <Separator />

            {/* Metadata */}
            {incident.metadata && Object.keys(incident.metadata).length > 0 && (
              <div>
                <Label className="text-xs text-muted-foreground mb-2 block">Metadata</Label>
                <pre className="text-xs bg-muted p-3 rounded-lg overflow-auto max-h-40">
                  {JSON.stringify(incident.metadata, null, 2)}
                </pre>
              </div>
            )}

            {/* Manager Note */}
            {incident.managerNote && (
              <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg">
                <Label className="text-xs text-amber-700 dark:text-amber-400 mb-1 block">Manager Note</Label>
                <p className="text-sm">{incident.managerNote}</p>
                {incident.reviewedBy && (
                  <p className="text-xs text-muted-foreground mt-2">
                    — {incident.reviewedBy} at {formatTimestamp(incident.reviewedAt)}
                  </p>
                )}
              </div>
            )}

            {/* Add Note Input */}
            {showNoteInput && (
              <div className="space-y-2">
                <Textarea
                  placeholder="Add a note..."
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  rows={3}
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleAddNote} disabled={!noteText.trim() || actionLoading === 'note added'}>
                    {actionLoading === 'note added' ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Save Note'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setShowNoteInput(false); setNoteText(''); }}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Actions */}
        <div className="pt-4 border-t mt-4 space-y-2">
          {!showNoteInput && (
            <Button variant="outline" size="sm" className="w-full" onClick={() => setShowNoteInput(true)}>
              <MessageSquare className="h-4 w-4 mr-2" />
              Add Note
            </Button>
          )}
          
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleAction('marked reviewed', onMarkReviewed, incident.incidentId, currentUser)}
              disabled={actionLoading === 'marked reviewed'}
            >
              <CheckCircle className="h-4 w-4 mr-1" />
              Mark Reviewed
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleAction('marked for follow-up', onMarkNeedsFollowup, incident.incidentId, currentUser, noteText || undefined)}
              disabled={actionLoading === 'marked for follow-up'}
            >
              <Flag className="h-4 w-4 mr-1" />
              Needs Follow-up
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              className="text-orange-600"
              onClick={() => handleAction('escalated', onMarkEscalated, incident.incidentId, currentUser, 'Escalated by manager')}
              disabled={actionLoading === 'escalated'}
            >
              <AlertTriangle className="h-4 w-4 mr-1" />
              Escalate
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              className="text-gray-600"
              onClick={() => handleAction('dismissed', onDismiss, incident.incidentId, currentUser, 'Dismissed by manager')}
              disabled={actionLoading === 'dismissed'}
            >
              <X className="h-4 w-4 mr-1" />
              Dismiss
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default IncidentDetailPanel;
